import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { SellerRole, SellerPermission } from '../auth/types';
import type { BrowserFingerprint } from '../auth/fingerprint';
import { createSecureStorage, clearSecureStorage, devtoolsConfig } from './secure-storage';

export interface AuthUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  role: SellerRole;
  permissions: SellerPermission[];
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpires: number | null;
  fingerprint: BrowserFingerprint | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// Define the persisted portion of state
type PersistedAuthState = Pick<
  AuthState,
  'user' | 'accessToken' | 'refreshToken' | 'accessTokenExpires' | 'fingerprint' | 'isAuthenticated'
>;

interface AuthActions {
  setAuth: (data: {
    user: AuthUser;
    accessToken: string;
    refreshToken?: string;
    accessTokenExpires?: number;
    fingerprint?: BrowserFingerprint;
  }) => void;
  updateTokens: (data: {
    accessToken: string;
    refreshToken?: string;
    accessTokenExpires?: number;
  }) => void;
  setFingerprint: (fingerprint: BrowserFingerprint) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
  hasPermission: (permission: SellerPermission) => boolean;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  accessTokenExpires: null,
  fingerprint: null,
  isAuthenticated: false,
  isLoading: true,
};

// Create secure storage instance for auth data
const authStorage = createSecureStorage<PersistedAuthState>('volovolo-pro-auth');

/**
 * Force reset auth state (used by tab-tracker when session should be cleared)
 * This must be called AFTER the store is created but BEFORE React renders
 */
export function forceResetAuthState(): void {
  // Reset the store to initial state
  useAuthStore.setState({
    user: null,
    accessToken: null,
    refreshToken: null,
    accessTokenExpires: null,
    fingerprint: null,
    isAuthenticated: false,
    isLoading: false,
  });
}

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        setAuth: (data) => {
          set({
            user: data.user,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken || null,
            accessTokenExpires: data.accessTokenExpires || null,
            fingerprint: data.fingerprint || null,
            isAuthenticated: true,
            isLoading: false,
          });
        },

        updateTokens: (data) => {
          set({
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? get().refreshToken,
            accessTokenExpires: data.accessTokenExpires ?? get().accessTokenExpires,
          });
        },

        setFingerprint: (fingerprint) => {
          set({ fingerprint });
        },

        setLoading: (loading) => {
          set({ isLoading: loading });
        },

        logout: () => {
          // Clear the storage first to ensure data is removed
          clearSecureStorage();
          // Then reset the state
          set({ ...initialState, isLoading: false });
        },

        hasPermission: (permission) => {
          const { user } = get();
          if (!user) return false;
          if (user.role === 'owner') return true;
          return user.permissions.includes(permission);
        },
      }),
      {
        name: 'volovolo-pro-auth',
        storage: authStorage,
        // Skip automatic hydration - tab-tracker will call rehydrate() manually
        skipHydration: true,
        partialize: (state): PersistedAuthState => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          accessTokenExpires: state.accessTokenExpires,
          fingerprint: state.fingerprint,
          isAuthenticated: state.isAuthenticated,
        }),
        onRehydrateStorage: () => () => {
          // Use setState to ensure React re-renders properly
          // Direct mutation (state.isLoading = false) doesn't trigger updates
          useAuthStore.setState({ isLoading: false });
        },
      }
    ),
    { ...devtoolsConfig, name: 'auth-store' }
  )
);
